// runtime-camera.js — camera boot with resolution fallback, the canvas draw
// loop (video frame + overlay compositing + FPS pacing/watchdog), scorer
// commentary mic, and full recording-screen teardown. Split from runtime-core.js.
import { get } from 'svelte/store';
import {
  currentScreen, cameraOk, cameraErr, isRecording,
  actualDrawFps, recordingPerformanceWarning, autoSplitInProgress,
  cameraInterruption, recordingHealth
} from './svelte/store.js';
import { appSettings, saveSettings, getResolutionPreset, RESOLUTION_PRESETS, RESOLUTION_FALLBACK_ORDER } from './store.js';
import { PeerSync } from './peer-sync.js';
import { toast } from './ui/dialogs.js';
import { withCanvasState, buildVideoConstraints } from './recording-utils.js';
import { createCameraInterruptionController } from './media/camera-interruption.js';
import { canvasElement as canvasEl, videoElement as videoEl, getCameraElements } from './media/camera-elements.js';
import { drawOverlay } from './runtime-overlay.js';
import { connectMicToGraph, disconnectMicFromGraph } from './runtime-audio.js';
import {
  runtimeState, camStream, setCamStream, setCamZoomCap,
  mediaRecorder, recTickId, setRecTickId, wakeLock, setWakeLock,
  _autoSplitTimer, setAutoSplitTimer, _cancelEndMatchTimers,
  _audioResumeTimer, setAudioResumeTimer
} from './store-runtime.js';

// ─── Draw-loop + camera-boot module state ───────────────────────────────
// Owned locally by this module (the only place the draw loop and camera boot
// run). Kept out of store-runtime.js because no other module touches them.
// drawFrameId holds a requestAnimationFrame handle; drawTimeoutId holds a
// setTimeout handle. They MUST stay separate — rAF and setTimeout are distinct
// id namespaces, so the old single-variable version called both clearTimeout()
// and cancelAnimationFrame() on the same number and routinely cancelled an
// unrelated timer (an over-summary delay, an innings-end transition, a toast).
let drawFrameId = null;
let drawTimeoutId = null;
let drawLoopWatchdog = null;
let _lastFrameAt = 0;
let _drawAverageMs = 0;
let _drawWindowStartedAt = 0;
let _drawWindowFrames = 0;
let _cameraBootAttempted = false;
let _cameraInterruptionController = null;

function publishCameraInterruption(state) {
  cameraInterruption.set(state);
  recordingHealth.update(current => {
    const wasCameraWarning = current?.signals?.cameraInterrupted === true;
    const cameraInterrupted = state.status !== 'live';
    let status = current?.status || 'idle';
    if (state.status === 'failed') status = 'action-required';
    else if (cameraInterrupted && status !== 'action-required') status = 'warning';
    else if (!cameraInterrupted && wasCameraWarning && status === 'warning') status = 'healthy';

    return {
      ...current,
      status,
      signals: {
        ...(current?.signals || {}),
        cameraInterrupted,
        cameraState: state.status,
        message: state.message || current?.signals?.message || ''
      },
      metrics: {
        ...(current?.metrics || {}),
        cameraInterruptedMs: state.totalInterruptedMs
      }
    };
  });
}

async function recoverEndedCamera(expectedStream) {
  if (expectedStream !== camStream || get(currentScreen) !== 'recording') return false;
  setCamStream(null);
  cameraOk.set(false);
  _cameraBootAttempted = false;
  try { disconnectMicFromGraph(); } catch (_) {}
  try { expectedStream.getTracks?.().forEach(track => track.stop?.()); } catch (_) {}
  const recovered = await bootCamera();
  return !!recovered?.getVideoTracks?.().some(track => track.readyState === 'live');
}

function attachCameraInterruptionHandlers(stream, camTrack) {
  _cameraInterruptionController = createCameraInterruptionController({
    onState: publishCameraInterruption,
    recover: () => recoverEndedCamera(stream)
  });
  _cameraInterruptionController.reset();
  if (!camTrack?.addEventListener) return;

  camTrack.addEventListener('mute', () => {
    if (stream === camStream) _cameraInterruptionController?.handleMute();
  });
  camTrack.addEventListener('unmute', () => {
    if (stream === camStream) _cameraInterruptionController?.handleUnmute();
  });
  camTrack.addEventListener('ended', async () => {
    if (stream !== camStream) return;
    const recovered = await _cameraInterruptionController?.handleEnded();
    if (!recovered && get(isRecording)) {
      const module = await import('./runtime-recording.js');
      module.stopRecording();
    }
  });
}

export function reportCameraRecorderError(error) {
  _cameraInterruptionController?.handleRecorderError(error);
}

export function stopDrawChain() {
  if (drawFrameId != null) {
    try { cancelAnimationFrame(drawFrameId); } catch (_) {}
    drawFrameId = null;
  }
  if (drawTimeoutId != null) {
    try { clearTimeout(drawTimeoutId); } catch (_) {}
    drawTimeoutId = null;
  }
}

export function startDrawLoop() {
  const liveCanvas = canvasEl;
  if (!liveCanvas) return;
  // Only ever run ONE draw chain. startDrawLoop is called from many places
  // (camera boot, recording start, canvas-resize retries); without this guard
  // each call would spawn a parallel rAF chain — doubling CPU and inflating the
  // measured FPS. Cancel any scheduled frame before starting a fresh chain.
  stopDrawChain();
  // The per-frame ctx/dimensions are re-read inside frame() (canvas can resize
  // between layouts), so no stale top-level copies here.
  const targetFps = Math.max(30, Number(runtimeState.recordingPresetOverride?.fps) || Number(appSettings.fps) || 30);
  const targetFrameMs = 1000 / Math.max(1, targetFps);

  // ── FRAME PACING ────────────────────────────────────────────────────────
  // rAF only fires on display refreshes, so a naive "draw when elapsed >= target"
  // test can round DOWN to a submultiple of the refresh rate.
  //
  // Lowering the threshold instead (e.g. target * 0.4) overshoots the other way —
  // at 30 fps it passes on EVERY 16.7 ms tick and renders 60 fps: double the CPU,
  // battery and heat, wasted because captureStream() samples at targetFps anyway,
  // and it breaks the actualDrawFps telemetry + recordingPerformanceWarning.
  //
  // Deadline scheduling fixes both: draw when THIS tick is closer to the deadline
  //
  // Lowering the threshold instead (e.g. target * 0.4) overshoots the other way —
  // at 30 fps it passes on EVERY 16.7 ms tick and renders 60 fps: double the CPU,
  // battery and heat, wasted because captureStream() samples at targetFps anyway,
  // and it breaks the actualDrawFps telemetry + recordingPerformanceWarning.
  //
  // Deadline scheduling fixes both: draw when THIS tick is closer to the deadline
  let nextDrawAt = 0;
  let lastTickTs = 0;
  let tickEstimate = 16.7; // adapts to 60/90/120 Hz panels

  function frame(timestamp, timerDriven = false) {
    const frameNow = Number(timestamp) || performance.now();
    _lastFrameAt = Date.now();

    if (lastTickTs) {
      const tickDelta = frameNow - lastTickTs;
      if (tickDelta > 0 && tickDelta < 100) tickEstimate = tickEstimate * 0.8 + tickDelta * 0.2;
    }
    lastTickTs = frameNow;
    if (!nextDrawAt) nextDrawAt = frameNow;

    if (!timerDriven && !document.hidden && frameNow + tickEstimate / 2 < nextDrawAt) {
      drawFrameId = requestAnimationFrame(frame);
      return;
    }
    nextDrawAt = (frameNow > nextDrawAt + targetFrameMs ? frameNow : nextDrawAt) + targetFrameMs;
    const drawStartedAt = performance.now();
    let missing = false;

    try {
      const c = canvasEl;
      const v = videoEl;

      if (!c || !v) {
        missing = true;
        if (get(currentScreen) === "recording") setTimeout(startDrawLoop, 200);
        return;
      }

      const _res = runtimeState.recordingPresetOverride || getResolutionPreset();
      if (c.width !== _res.w || c.height !== _res.h) {
        c.width = _res.w;
        c.height = _res.h;
      }

      const cctx = c.getContext("2d", { alpha: false, desynchronized: true }) || c.getContext("2d");
      cctx.imageSmoothingEnabled = true;
      cctx.imageSmoothingQuality = "medium";
      cctx.filter = "none";

      if (v && camStream) {
        if (v.srcObject !== camStream) {
          try { v.srcObject = camStream; } catch (_) {}
        }
        if (v.paused && v.readyState >= 1) {
          try { v.play().catch(() => {}); } catch (_) {}
        }
      }

      if (v && v.readyState >= 2 && v.videoWidth && v.videoHeight) {
        const vw = v.videoWidth, vh = v.videoHeight;
        const scale = Math.max(c.width / vw, c.height / vh);
        const dw = vw * scale, dh = vh * scale;
        const dx = (c.width - dw) / 2, dy = (c.height - dh) / 2;
        cctx.fillStyle = "#000";
        cctx.fillRect(0, 0, c.width, c.height);
        cctx.drawImage(v, dx, dy, dw, dh);
        cctx.filter = "none";
      } else {
        cctx.fillStyle = "#0a0a0a";
        cctx.fillRect(0, 0, c.width, c.height);
      }

      withCanvasState(cctx, () => {
        const isVertical = appSettings.recordingLayout === 'vertical';
        const logicalW = isVertical ? 720 : 1280;
        const logicalH = isVertical ? 1280 : 720;
        const sScale = c.width / logicalW;
        cctx.scale(sScale, sScale);
        drawOverlay(cctx, logicalW, logicalH);
      });
    } catch (err) {
      console.error("Draw frame error (will retry):", err);
    } finally {
      const drawMs = Math.max(0, performance.now() - drawStartedAt);
      _drawAverageMs = _drawAverageMs ? (_drawAverageMs * 0.85 + drawMs * 0.15) : drawMs;
      const sampleNow = performance.now();
      if (!_drawWindowStartedAt) _drawWindowStartedAt = sampleNow;
      _drawWindowFrames++;
      if (sampleNow - _drawWindowStartedAt >= 1000) {
        runtimeState.actualDrawFps = Math.round((_drawWindowFrames * 10000) / (sampleNow - _drawWindowStartedAt)) / 10;
        const expectedFps = Number(runtimeState.recordingPresetOverride?.fps) || Number(appSettings.fps) || 30;
        runtimeState.recordingPerformanceWarning = !!(get(isRecording) && runtimeState.actualDrawFps < expectedFps * 0.8);
        actualDrawFps.set(runtimeState.actualDrawFps);
        recordingPerformanceWarning.set(runtimeState.recordingPerformanceWarning);
        _drawWindowStartedAt = sampleNow;
        _drawWindowFrames = 0;
      }
      if (missing) {
        stopDrawChain();
      } else if (get(isRecording)) {
        drawFrameId = null;
        const delay = Math.max(0, Math.min(targetFrameMs, nextDrawAt - performance.now()));
        drawTimeoutId = setTimeout(() => frame(performance.now(), true), delay);
      } else if (document.hidden) {
      }
    }
  }
  frame();

  // Low-frequency watchdog: Android WebViews throttle requestAnimationFrame to a
  // near-standstill when the browser chrome overlaps the canvas (without ever
  // firing `visibilitychange`), which would freeze the recorded overlay. If the
  // chain has not produced a frame for >1.5s while frames are still needed, kick
  // it back to life. Only one watchdog ever runs; it is cleared on teardown.
  if (drawLoopWatchdog == null && typeof setInterval === 'function') {
    drawLoopWatchdog = setInterval(() => {
      const stalled = _lastFrameAt && (Date.now() - _lastFrameAt > 1500);
      const needFrames = get(currentScreen) === 'recording' || get(isRecording);
      if (stalled && needFrames && canvasEl) startDrawLoop();
    }, 2000);
  }
}

// Boot the live camera stream and start the canvas draw loop. Idempotent and
// concurrency-safe: overlapping calls (RecorderScreen + BroadcastOverlay both
// mount and call this) never trigger a second getUserMedia. On failure it
// clears the attempt flag so retryCamera / a later mount can try again.
export async function bootCamera() {
  if (camStream) {
    try { startDrawLoop(); } catch (_) {}
    return camStream;
  }
  if (_cameraBootAttempted) return camStream;
  _cameraBootAttempted = true;
  cameraErr.set(null);
  // Held outside the try so a later teardown/recovery path can still reach the
  // active video track (zoom + torch constraints are applied through it).
  let camTrack = null;
  try {
    const stream = await requestCameraWithFallback({
      echoCancellation: false,
      noiseSuppression: false,
      autoGainControl: false
    });
    setCamStream(stream);
    camTrack = stream.getVideoTracks?.()?.[0] || null;
    attachCameraInterruptionHandlers(stream, camTrack);

    // Route the microphone into the recording mix. Without this the saved video
    // has a silent audio track — the mic was captured but never connected.
    try { connectMicToGraph(stream); } catch (_) {}

    // Attach to the preview <video> so the draw loop has decoded frames to
    // composite. The draw loop also re-attaches defensively each frame.
    const { videoEl } = getCameraElements();
    if (videoEl) {
      try { videoEl.srcObject = stream; } catch (_) {}
      try { await videoEl.play?.().catch(() => {}); } catch (_) {}
    }

    // Expose optical/digital zoom capability to the on-screen zoom buttons.
    try {
      const zoom = camTrack?.getCapabilities?.()?.zoom;
      if (zoom && Number.isFinite(zoom.max) && zoom.max > (zoom.min ?? 1)) {
        setCamZoomCap({ min: zoom.min ?? 1, max: zoom.max, step: zoom.step || 0.1 });
      } else {
        setCamZoomCap(null);
      }
    } catch (_) {
      setCamZoomCap(null);
    }

    cameraOk.set(true);
    cameraErr.set(null);
    startDrawLoop();
    return stream;
  } catch (err) {
    _cameraBootAttempted = false;
    cameraOk.set(false);
    cameraErr.set(err?.message || 'Camera unavailable. Check permissions and try again.');
    console.error('[Camera] boot failed:', err);
    return null;
  }
}

// Explicit user-triggered retry after a boot failure (the "Retry" button on the
// recording screen). Tears down any half-open stream then boots fresh.
export async function retryCamera() {
  _cameraBootAttempted = false;
  cameraErr.set(null);
  cameraOk.set(false);
  try { disconnectMicFromGraph(); } catch (_) {}
  const previousStream = camStream;
  setCamStream(null);
  try { previousStream?.getTracks().forEach(t => { try { t.stop(); } catch (_) {} }); } catch (_) {}
  setCamZoomCap(null);
  return bootCamera();
}

async function requestCameraWithFallback(audio) {
  const selectedIndex = RESOLUTION_FALLBACK_ORDER.indexOf(appSettings.resolution);
  const startIndex = selectedIndex >= 0 ? selectedIndex : RESOLUTION_FALLBACK_ORDER.indexOf("1080p");
  // Try the user-picked camera source first; if that exact device is gone
  // (unplugged webcam / stale deviceId) retry once with the default back camera
  // so recording never dead-ends on a missing source.
  const deviceCandidates = appSettings.cameraDeviceId ? [appSettings.cameraDeviceId, ""] : [""];
  let lastError = null;
  for (const deviceId of deviceCandidates) {
    for (const presetKey of RESOLUTION_FALLBACK_ORDER.slice(startIndex)) {
      let preset = RESOLUTION_PRESETS[presetKey];
      if (appSettings.recordingLayout === 'vertical') {
        preset = { ...preset, w: preset.h, h: preset.w };
      }
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          video: buildVideoConstraints({ deviceId, preset, fps: appSettings.fps }),
          audio
        });
        runtimeState.actualCaptureResolution = presetKey;
        return stream;
      } catch (err) {
        lastError = err;
      }
    }
  }
  throw lastError || new Error("No supported camera resolution found");
}

// Enumerate available video input devices (labels populate only after camera
// permission is granted). Used by the Settings + recording-screen source picker.
export async function listVideoInputs() {
  try {
    const devices = await navigator.mediaDevices?.enumerateDevices?.();
    if (!Array.isArray(devices)) return [];
    return devices
      .filter(d => d.kind === 'videoinput')
      .map((d, i) => ({ deviceId: d.deviceId, label: d.label || `Camera ${i + 1}` }));
  } catch (_) {
    return [];
  }
}

// Switch the active camera source (before recording only — MediaRecorder is
// bound to one stream). Persists the choice and re-boots the camera.
export async function switchCamera(deviceId) {
  if (get(isRecording)) {
    toast("Stop recording before switching camera.", "error");
    return false;
  }
  appSettings.cameraDeviceId = deviceId || "";
  saveSettings();
  try { disconnectMicFromGraph(); } catch (_) {}
  const previousStream = camStream;
  setCamStream(null);
  try { previousStream?.getTracks().forEach(t => { try { t.stop(); } catch (_) {} }); } catch (_) {}
  setCamZoomCap(null);
  cameraErr.set(null);
  cameraOk.set(false);
  _cameraBootAttempted = false;
  await bootCamera();
  return true;
}

export function stopScorerMic() {
  // Close the PeerJS MediaConnection too. Stopping only the local tracks left a
  // live RTCPeerConnection behind on every mic toggle; the native WebRTC stack
  // never reclaimed them over the course of a match.
  try { runtimeState._scorerMicCall?.close(); } catch (_) {}
  runtimeState._scorerMicCall = null;
}





export async function toggleScorerMic() {
  if (runtimeState._scorerMicActive) {
    stopScorerMic();
    return false;
  }
  if (!PeerSync.isConnected) {
    toast("Connect the recorder phone before enabling commentary.", "error");
    return false;
  }
  try {
    const stream = await navigator.mediaDevices.getUserMedia({
      video: false,
      audio: { echoCancellation: false, noiseSuppression: false, autoGainControl: false }
    });
    try {
      const call = PeerSync.sendAudioStream(stream);
      if (!call) {
        stream.getTracks().forEach(track => track.stop());
        throw new Error("Recorder audio channel is not ready");
      }
      runtimeState._scorerMicCall = call; // retained so stopScorerMic can close it
      runtimeState._scorerMicStream = stream;
      runtimeState._scorerMicActive = true;
      return true;
    } catch (innerErr) {
      stream.getTracks().forEach(track => track.stop());
      throw innerErr;
    }
  } catch (err) {
    runtimeState._scorerMicActive = false;
    toast("Microphone unavailable: " + (err?.message || err), "error");
    return false;
  }
}

export async function leaveRecordingScreen() {
  if (_autoSplitTimer) { clearTimeout(_autoSplitTimer); setAutoSplitTimer(null); }
  if (runtimeState._chainRestartTimer) { clearTimeout(runtimeState._chainRestartTimer); runtimeState._chainRestartTimer = null; }
  if (runtimeState._callingTimeInterval) { clearInterval(runtimeState._callingTimeInterval); runtimeState._callingTimeInterval = null; }
  _cancelEndMatchTimers();
  runtimeState._autoSplitting = false;
  runtimeState._splitOverNumber = null;
  autoSplitInProgress.set(false);
  runtimeState._autoSplitCountdown = 0;
  stopDrawChain();
  if (drawLoopWatchdog != null) { clearInterval(drawLoopWatchdog); drawLoopWatchdog = null; }
  // stopRecording() clears this, but leaving via the screen exit does not go
  // through stopRecording — the interval would tick forever in the background.
  if (_audioResumeTimer) { clearInterval(_audioResumeTimer); setAudioResumeTimer(null); }

  if (get(isRecording) && mediaRecorder && mediaRecorder.state !== 'inactive') {
    // Mobile WebViews sometimes never fire `onstop`. Without the timeout this
    // await hangs forever and the camera is never torn down.
    await Promise.race([
      new Promise(resolve => {
        const originalOnStop = mediaRecorder.onstop;
        mediaRecorder.onstop = async (e) => {
          if (originalOnStop) {
            try { await originalOnStop(e); } catch (_) {}
          }
          resolve();
        };
        try { mediaRecorder.stop(); } catch (e) { resolve(); }
      }),
      new Promise(resolve => setTimeout(() => {
        console.warn('[Recording] onstop never fired — continuing teardown after 5s.');
        resolve();
      }, 5000))
    ]);
    isRecording.set(false);
  } else if (get(isRecording)) {
    isRecording.set(false);
  }

  if (recTickId) { clearInterval(recTickId); setRecTickId(null); }
  try { disconnectMicFromGraph(); } catch (_) {}
  const previousStream = camStream;
  setCamStream(null);
  if (previousStream) { try { previousStream.getTracks().forEach(t => t.stop()); } catch (e) {} }
  if (wakeLock && wakeLock.release) { try { wakeLock.release(); } catch (e) {} setWakeLock(null); }
  _cameraBootAttempted = false;
  cameraErr.set(null);
  
  stopScorerMic();
}
